import {AxiosResponse} from 'axios';
import {makeAutoObservable} from 'mobx';
import {DateFormats} from 'constants/dateFormats';
import {Status} from 'constants/status';
import {LogsService} from 'store/apiClients/logs';
import {Log, LogsListProps} from 'store/apiClients/logs/types';
import {LogActionType} from 'store/apiClients/logs/enum';
import {Pagination} from 'store/utilityClasses/Pagination';
import {formatRu} from 'utils/date/formatters';
import {saveAs} from 'file-saver';
import {getFileNameFromResponseHeader} from 'utils/fileWorkers';

export class LogsStore {
    status = Status.Initial;
    pagination = new Pagination();
    logs: Log[] = [];
    actionType: LogActionType | null = null;
    startDate: Date | null = new Date();
    endDate: Date | null = null;

    readonly service = new LogsService();

    get isLoading(): boolean {
        return this.status === Status.Pending;
    }

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    * fetchLogs(): Generator {
        try {
            this.status = Status.Pending;

            const { data, headers } = (yield this.service.getList({
                ...this.startDate && {date_from: formatRu(this.startDate.toISOString(), DateFormats.isoDate)},
                ...this.endDate && {date_to: formatRu(this.endDate.toISOString(), DateFormats.isoDate)},
                ...this.actionType && {action_type: this.actionType},
                page: this.pagination.page,
                per_page: 50
            } as LogsListProps)) as AxiosResponse<Log[]>;
            this.pagination.setPages(headers);
            this.logs = data;
            this.status = Status.Fulfilled;
        } catch(err) {
            this.status = Status.Rejected;
            console.error(err);
        }
    }

    * downloadReport(): Generator {
        try {
            this.status = Status.Pending;

            const { data, headers } = (yield this.service.getReportInCsv({
                ...this.startDate && {date_from: formatRu(this.startDate.toISOString(), DateFormats.isoDate)},
                ...this.endDate && {date_to: formatRu(this.endDate.toISOString(), DateFormats.isoDate)},
                ...this.actionType && {action_type: this.actionType},
            })) as AxiosResponse;

            const fileName = getFileNameFromResponseHeader(headers, 'журнал логирования.csv');
            saveAs(data, fileName);

            this.status = Status.Fulfilled;
        } catch (err) {
            this.status = Status.Rejected;
            console.error(err);
        }
    }

    setStartDate(date: Date | null): void {
        this.startDate = date;
        this.pagination.resetPage();

        if (this.actionType) {
            this.fetchLogs()
        }
    }

    setEndDate(date: Date | null): void {
        this.endDate = date;
        this.pagination.resetPage();

        if (this.actionType) {
            this.fetchLogs()
        }
    }

    setActionType(type: LogActionType): void {
        this.actionType = type;
        this.pagination.resetPage();
        this.fetchLogs();
    }

    resetFilters(): void {
        this.startDate = new Date();
        this.endDate = null;
        this.actionType = null;

        this.pagination.resetPage();
        this.pagination.pages = 0;
        this.logs = [];
    }
}